import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useAuth } from './AuthContext';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';

// Re-using some styles from Account.js and CheckoutForm.js for consistency
const PageContainer = styled.div`
  max-width: 800px;
  margin: 4rem auto;
  padding: 2rem;
  background-color: #f8f9fa;
  border-radius: 8px;
`;

const Title = styled.h1`
  margin-bottom: 2rem;
  border-bottom: 1px solid #e0e0e0;
  padding-bottom: 1rem;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 600;
  color: #444;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px 15px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 1rem;
  transition: border-color 0.2s, box-shadow 0.2s;

  &:focus {
    outline: none;
    border-color: #005eb8;
    box-shadow: 0 0 0 2px rgba(0, 94, 184, 0.2);
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 12px 15px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 1rem;
  background-color: white;
  transition: border-color 0.2s, box-shadow 0.2s;

  &:focus {
    outline: none;
    border-color: #005eb8;
    box-shadow: 0 0 0 2px rgba(0, 94, 184, 0.2);
  }
`;

const SaveButton = styled.button`
  padding: 15px;
  background-color: #005eb8;
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 1.1rem;
  font-weight: bold;
  cursor: pointer;
  margin-top: 1rem;
  align-self: flex-start;

  &:disabled {
    background-color: #ccc;
    cursor: not-allowed;
  }
`;

const AccountDetails = () => {
  const { user, updateUserDetails, loading, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    dob: '',
    email: '',
    phone: '',
    gender: '',
    line1: '',
    line2: '',
    city: '',
    state: '',
    postal_code: '',
    country: '',
  });
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!loading && !isAuthenticated) {
      navigate('/login');
    }
  }, [isAuthenticated, loading, navigate]);

  useEffect(() => {
    if (user) {
      setFormData({
        name: user.name || '',
        dob: user.dob ? user.dob.split('T')[0] : '',
        email: user.email || '',
        phone: user.phone || '',
        gender: user.sex || '',
        line1: user.shipping_address?.line1 || '',
        line2: user.shipping_address?.line2 || '',
        city: user.shipping_address?.city || '',
        state: user.shipping_address?.state || '',
        postal_code: user.shipping_address?.postal_code || '',
        country: user.shipping_address?.country || '',
      });
    }
  }, [user]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSaving(true);

    const payload = {
      name: formData.name,
      dob: formData.dob,
      email: formData.email,
      phone: formData.phone,
      sex: formData.gender,
      shipping_address: {
        line1: formData.line1,
        line2: formData.line2,
        city: formData.city,
        state: formData.state,
        postal_code: formData.postal_code,
        country: formData.country,
      }
    };

    const result = await updateUserDetails(payload);
    if (result.success) {
      toast.success('Your details have been updated successfully!');
      navigate('/account');
    } else {
      toast.error(result.error || 'Failed to update details.');
    }
    setIsSaving(false);
  };

  if (loading || !user) {
    return <PageContainer><p>Loading...</p></PageContainer>;
  }

  return (
    <PageContainer>
      <Title>Change My Details</Title>
      <Form onSubmit={handleSubmit}>
        <FormGroup>
          <Label htmlFor="name">Full Name</Label>
          <Input id="name" name="name" type="text" value={formData.name} onChange={handleChange} required />
        </FormGroup>
        <FormGroup>
          <Label htmlFor="dob">Date of Birth</Label>
          <Input id="dob" name="dob" type="date" value={formData.dob} onChange={handleChange} required />
        </FormGroup>
        <FormGroup>
          <Label htmlFor="email">Email Address</Label>
          <Input id="email" name="email" type="email" value={formData.email} onChange={handleChange} required />
        </FormGroup>
        <FormGroup>
          <Label htmlFor="phone">Mobile Number</Label>
          <Input id="phone" name="phone" type="tel" value={formData.phone} onChange={handleChange} />
        </FormGroup>
        <FormGroup>
          <Label htmlFor="gender">Gender</Label>
          <Select id="gender" name="gender" value={formData.gender} onChange={handleChange} required >
            <option value="" disabled>Select...</option>
            <option value="Male">Male</option>
            <option value="Female">Female</option>
            <option value="Other">Other</option>
            <option value="Prefer not to say">Prefer not to say</option>
          </Select>
        </FormGroup>
        <FormGroup>
          <Label>Shipping Address</Label>
          <div style={{ display: 'grid', gap: '1rem' }}>
            <Input name="line1" placeholder="Address Line 1" value={formData.line1} onChange={handleChange} required />
            <Input name="line2" placeholder="Address Line 2 (Optional)" value={formData.line2} onChange={handleChange} />
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <Input name="city" placeholder="City" value={formData.city} onChange={handleChange} required />
              <Input name="state" placeholder="County/State" value={formData.state} onChange={handleChange} />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <Input name="postal_code" placeholder="Postcode" value={formData.postal_code} onChange={handleChange} required />
              <Input name="country" placeholder="Country" value={formData.country} onChange={handleChange} required />
            </div>
          </div>
        </FormGroup>
        <SaveButton type="submit" disabled={isSaving}>
          {isSaving ? 'Saving...' : 'Save Changes'}
        </SaveButton>
      </Form>
    </PageContainer>
  );
};

export default AccountDetails;